import React, { FC, useState, useEffect, useMemo } from 'react';

import { CapacityToPayCard } from 'ContentfulDefaults/types/profile';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import { Paragraph } from 'DesignLibrary/atoms';
import TextInput from 'DesignLibrary/atoms/inputs/TextInput';
import ToggleList from 'DesignLibrary/atoms/ToggleList';
import { useViewport } from 'DesignLibrary/context';
import { sendInsightsEvent } from 'Utils/insights';

import Question from '../_shared/Question';
import { QuestionWrapper } from '../_shared/Question/styled';
import { AudioConfigTooltipContentfulPaths } from '../ProfileAudio';

interface HsaFundsSectionProps {
  handleSectionChange: (isValid: boolean, isComplete: boolean) => void;
  hasOopCost: boolean | null;
  isNextDirty: boolean;
  handleHsaFundsChange: (value: { hasHsaFunds: 'yes' | 'no' | null; hsaAmount: string }) => void;
  hsaFunds: { hasHsaFunds: 'yes' | 'no' | null; hsaAmount: string };
  isProfileAudioEnabled: boolean;
}

const HsaFundsSection: FC<HsaFundsSectionProps> = ({
  handleSectionChange,
  hasOopCost,
  isNextDirty,
  handleHsaFundsChange,
  hsaFunds,
  isProfileAudioEnabled,
}) => {
  const { retrieveContentfulData } = useTextContext();
  const { is_hsa_funds_question_enabled } = useFeatureFlagContext();
  const { device } = useViewport();

  const shouldShowSection = is_hsa_funds_question_enabled && hasOopCost === true;
  const [hasHsaFunds, setHasHsaFunds] = useState(hsaFunds?.hasHsaFunds ?? null);
  const [hsaAmount, setHsaAmount] = useState(hsaFunds?.hsaAmount ?? '');
  const [hasBlurred, setHasBlurred] = useState(hsaAmount.trim().length > 0);

  useEffect(() => {
    if (!shouldShowSection) {
      handleSectionChange(true, true);
    }
  }, [shouldShowSection]);

  const capacityToPayContent = retrieveContentfulData<CapacityToPayCard>('profile_section.capacity_to_pay');

  const questionText = capacityToPayContent?.hsa_subheader;
  const inputLabel = capacityToPayContent?.hsa_amount_question;
  // Pure validation - is the current value valid? (no hasBlurred dependency)
  const isValueValid = useMemo(() => {
    if (hasHsaFunds === 'no') return true;
    if (hasHsaFunds === 'yes') {
      const numericValue = Number(hsaAmount.replace(/,/g, ''));
      return !Number.isNaN(numericValue) && numericValue > 0;
    }
    return false;
  }, [hasHsaFunds, hsaAmount]);

  // Section validity - requires blur to confirm user is done typing
  const isValid = useMemo(() => {
    if (hasHsaFunds === 'no') return true;
    if (hasHsaFunds === 'yes' && hasBlurred && isValueValid) return true;
    return false;
  }, [hasHsaFunds, hasBlurred, isValueValid]);

  const isComplete = useMemo(() => {
    return hasHsaFunds !== null && isValid;
  }, [hasHsaFunds, isValid]);

  // Single useEffect for section change logic
  useEffect(() => {
    if (!shouldShowSection) return;

    // If user selected 'yes' but hasn't entered an amount yet, section is invalid
    if (hasHsaFunds === 'yes' && !hasBlurred && !hsaAmount.trim()) {
      handleSectionChange(false, false);
      return;
    }

    handleSectionChange(isValid, isComplete);
  }, [hasHsaFunds, hsaAmount, hasBlurred, shouldShowSection, isValid, isComplete]);

  if (!shouldShowSection) return null;

  const handleOptionChange = (option: 'yes' | 'no') => {
    sendInsightsEvent(null, 'has_hsa_funds', { feedback: option });
    setHasHsaFunds(option);
    if (option === 'no') {
      setHsaAmount('');
      handleHsaFundsChange({ hasHsaFunds: 'no', hsaAmount: '' });
    }
  };

  const handleAmountChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    value: string | number | null | undefined,
  ) => {
    // user is editing again → mark as not finalized
    if (hasBlurred) {
      setHasBlurred(false);
    }
    setHsaAmount(value?.toString() || '');
  };

  const handleAmountBlur = () => {
    // Remove commas from formatted value for storage
    const currentValue = hsaAmount.replace(/,/g, '');

    // Check if the value will be invalid
    const numericValue = Number(currentValue);
    const willBeInvalid = Number.isNaN(numericValue) || numericValue <= 0;

    if (willBeInvalid) {
      // Just mark as blurred - useEffect will send (false, false)
      // Don't call handleHsaFundsChange for invalid values
      setHasBlurred(true);
    } else {
      setHasBlurred(true);
      handleHsaFundsChange({
        hasHsaFunds: 'yes',
        hsaAmount: currentValue,
      });
    }

    sendInsightsEvent(null, 'hsa_allocation_amount', { hsaAmount: currentValue });
  };

  const optionY = (capacityToPayContent?.button_options && capacityToPayContent?.button_options.yes) || 'Yes';
  const optionN = (capacityToPayContent?.button_options && capacityToPayContent?.button_options.no) || 'No';
  const isIncomplete = hasHsaFunds === null || (hasHsaFunds === 'yes' && hsaAmount.trim() === '');

  return (
    <div id="hsaFunds-section" data-testid="hsaFunds-section">
      <Question
        error={!isValueValid && (isNextDirty || hasBlurred)}
        name="hsa_funds"
        isIncomplete={isIncomplete}
        audioTooltipsEnabled={isProfileAudioEnabled}
        audioTooltipContentfulPath={AudioConfigTooltipContentfulPaths.HsaFundsSectionAudioTooltip}
        data-testid="hsa-funds-section"
      >
        <QuestionWrapper className="question-wrapper">
          <Paragraph>{questionText}</Paragraph>

          <ToggleList
            label={questionText}
            groupName="hsaFundsQuestion"
            options={[
              { option: optionY as string, value: 'yes' },
              { option: optionN as string, value: 'no' },
            ]}
            handleChange={(e) => handleOptionChange(e.target.value as 'yes' | 'no')}
            color="--colors-gray-black"
            selected={hasHsaFunds ?? undefined}
            stretch={device === 'mobile'}
          />
        </QuestionWrapper>

        {hasHsaFunds === 'yes' && (
          <div style={{ marginTop: '24px' }}>
            <Paragraph style={{ marginBottom: '15px' }}>{inputLabel}</Paragraph>

            <TextInput
              error={!isValueValid && (isNextDirty || hasBlurred)}
              data-testid="hsa-amount-input"
              inputType="currency"
              inputMode="numeric"
              type="tel"
              lang="en-150"
              name="hsaAmount"
              placeholder="0"
              value={hsaAmount}
              handleChange={handleAmountChange}
              onBlur={handleAmountBlur}
            />
          </div>
        )}
      </Question>
    </div>
  );
};
export default HsaFundsSection;
